import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Landscape as PLCIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import AlertModal from "../../components/AlertModal";
import ConfirmModal from "../../components/ConfirmModal";
import { hasWritePermission } from "../../utils/menuFilter";

const PLC = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [plotData, setPlotData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});

  const [openModal, setOpenModal] = useState(false);
  const [editingPLC, setEditingPLC] = useState(null);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [newPLC, setNewPLC] = useState({
    plcName: "",
    category: "",
    status: "Active",
    priority: "",
  });

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedPLCForDelete, setSelectedPLCForDelete] = useState(null);

  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const fetchPLCs = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseURL}/api/globalMasterDetails?type=plc`);
      if (!response.ok) {
        throw new Error(`Failed to fetch PLCs: ${response.statusText}`);
      }
      const data = await response.json();
      const plcs = Array.isArray(data.data)
        ? data.data.map((item) => ({
          id: item._id,
          plcName: item.details.plcName || "N/A",
          category: item.details.category || "N/A",
          priority: parseInt(item.details.priority) || 0,
          status: item.status ? "Active" : "Inactive",
        }))
        : [];
      setPlotData(plcs);
    } catch (err) {
      setError("Failed to fetch PLCs. Please try again later.");
      console.error("Error fetching PLCs:", err);
      setPlotData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPLCs();
  }, []);

  const getFilteredPLCs = () => {
    if (!Array.isArray(plotData)) {
      console.warn("plotData is not an array:", plotData);
      return [];
    }
    return plotData.filter((plc) => {
      const matchesSearch = Object.values(plc).some((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchesCategory = !categoryFilter || plc.category === categoryFilter;
      const matchesStatus = statusFilter === "all" || plc.status === statusFilter;
      return matchesSearch && matchesCategory && matchesStatus;
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Active":
        return { backgroundColor: "#e8f5e9", color: "#2e7d32" };
      case "Inactive":
        return { backgroundColor: "#ffebee", color: "#d32f2f" };
      default:
        return { backgroundColor: "#f5f5f5", color: "#666" };
    }
  };

  const getCategoryStyle = (category) => {
    switch (category) {
      case "Premium":
        return { backgroundColor: "#f3e5f5", color: "#7b1fa2" };
      case "Standard":
        return { backgroundColor: "#e3f2fd", color: "#1976d2" };
      case "Basic":
        return { backgroundColor: "#fff3e0", color: "#f57c00" };
      default:
        return { backgroundColor: "#f5f5f5", color: "#666" };
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newPLC.plcName.trim()) newErrors.plcName = "PLC name is required";
    if (!newPLC.category) newErrors.category = "Category is required";
    if (!newPLC.priority || parseInt(newPLC.priority) <= 0)
      newErrors.priority = "Valid priority is required";
    return newErrors;
  };

  const handleEdit = (plc) => {
    setEditingPLC(plc);
    setNewPLC({
      plcName: plc.plcName || "",
      category: plc.category || "",
      status: plc.status || "Active",
      priority: plc.priority.toString() || "",
    });
    setOpenModal(true);
  };

  const handleDeleteClick = (plc) => {
    setSelectedPLCForDelete(plc);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPLCForDelete) return;
    
    setIsLoading(true);
    setConfirmOpen(false);
    try {
      const response = await fetch(`${baseURL}/api/globalMasterDetails/${selectedPLCForDelete.id}`, {
        method: "PUT",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDeleted: true }),
      });
      if (!response.ok) {
        throw new Error(`Failed to delete PLC: ${response.statusText}`);
      }
      await fetchPLCs(true); 
      setAlertConfig({ title: 'Success', message: 'PLC deleted successfully!' });
      setAlertOpen(true);
    } catch (error) {
      setAlertConfig({ title: 'Error', message: error.message || 'Failed to delete PLC. Please try again.' });
      setAlertOpen(true);
      console.error("Error deleting PLC:", error);
    } finally {
      setIsLoading(false);
      setSelectedPLCForDelete(null);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    const count = getFilteredPLCs().length;
    setAlertConfig({ 
      title: 'Search Results', 
      message: count > 0 ? `Found ${count} PLCs matching your criteria.` : 'No PLCs found matching your criteria.' 
    });
    setAlertOpen(true);
  };

  const handleReset = () => {
    setSearchQuery("");
    setCategoryFilter("");
    setStatusFilter("all");
    setPage(0);
    setErrors({});
    fetchPLCs(true);
  };

  const handleOpenModal = () => {
    setEditingPLC(null);
    setNewPLC({
      plcName: "",
      category: "",
      status: "Active",
      priority: "",
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingPLC(null);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPLC({ ...newPLC, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleAddPLC = async () => {
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsLoadingModal(true);
    try {
      const payload = {
        type: "plc",
        details: {
          plcName: newPLC.plcName,
          category: newPLC.category,
          priority: newPLC.priority.toString(), // Send as string to match cURL
        },
        status: newPLC.status === "Active",
        createdBy: "adminUser", // Replace with dynamic user if needed
      };

      const url = editingPLC
        ? `${baseURL}/api/globalMasterDetails/${editingPLC.id}`
        : `${baseURL}/api/globalMasterDetails`;
      const method = editingPLC ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `Failed to ${editingPLC ? "update" : "add"} PLC: ${response.statusText}`
        );
      }

      await fetchPLCs(true); // Refresh silently
      handleCloseModal();
      setAlertConfig({ 
        title: 'Success', 
        message: editingPLC ? "PLC updated successfully!" : "PLC added successfully!" 
      });
      setAlertOpen(true);
    } catch (error) {
      setErrors({ general: error.message || "An error occurred. Please try again." });
      console.error("Error saving PLC:", error);
    } finally {
      setIsLoadingModal(false);
    }
  };

  const filteredPLCs = getFilteredPLCs();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      {/* Header Section */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PLCIcon sx={{ mr: 1, color: "primary.main", fontSize: 28 }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            PLC (Plot Location Charges) Management
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchPLCs}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                flex: { xs: 1, sm: 'none' }
              }}
            >
              Add PLC
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal Alerts */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the PLC <strong>{selectedPLCForDelete?.plcName}</strong>? This action cannot be undone.
      </ConfirmModal>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search PLCs..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Filter by Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="Premium">Premium</MenuItem>
                <MenuItem value="Standard">Standard</MenuItem>
                <MenuItem value="Basic">Basic</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Filter by Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ flex: 1, borderRadius: 2 }}
              >
                Search
              </Button>
              {/* <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                sx={{ flex: 1, borderRadius: 2 }}
              >
                Reset
              </Button> */}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading Indicator */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table Section */}
      {!isLoading && (
        <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>PLC Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: "0.95rem" }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPLCs.length > 0 ? (
                  filteredPLCs
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((plc, index) => (
                      <TableRow key={plc.id} hover>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">{plc.plcName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={plc.category}
                            size="small"
                            style={getCategoryStyle(plc.category)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`Priority ${plc.priority}`}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={plc.status}
                            size="small"
                            style={getStatusStyle(plc.status)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            {canWrite && (
                              <Tooltip title="Edit PLC">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleEdit(plc)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canWrite && (
                              <Tooltip title="Delete PLC">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleDeleteClick(plc)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No PLCs found matching your criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredPLCs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Add/Edit PLC Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            fontWeight: "bold",
          }}
        >
          {editingPLC ? "Edit PLC" : "Add New PLC"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="PLC Name *"
                name="plcName"
                value={newPLC.plcName}
                onChange={handleInputChange}
                error={!!errors.plcName}
                helperText={errors.plcName}
                margin="normal"
                placeholder="e.g., Corner + Park Facing"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" error={!!errors.category}>
                <InputLabel>Category *</InputLabel>
                <Select
                  name="category"
                  value={newPLC.category}
                  onChange={handleInputChange}
                  label="Category *"
                >
                  <MenuItem value="Premium">Premium</MenuItem>
                  <MenuItem value="Standard">Standard</MenuItem>
                  <MenuItem value="Basic">Basic</MenuItem>
                </Select>
                {errors.category && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.category}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Priority *"
                name="priority"
                type="number"
                value={newPLC.priority}
                onChange={handleInputChange}
                error={!!errors.priority}
                helperText={errors.priority}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={newPLC.status}
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {errors.general && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.general}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} disabled={isLoadingModal}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddPLC}
            disabled={isLoadingModal}
            startIcon={isLoadingModal ? <CircularProgress size={20} /> : null}
          >
            {isLoadingModal ? "Saving..." : editingPLC ? "Update PLC" : "Add PLC"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PLC;